/**
 * TextureEngine.js
 * Generates idiomatic orchestral textures (Ostinatos, Runs, Stabs)
 * to replace generic arpeggios.
 */

import { ABSTRACT_ROLES } from '../RoleAllocator.js';
import { isRangeBusy } from '../RegistryUtils.js';

const TEXTURE_CONFIG = {
    fallback: {
        third: 4, // Major 3rd
        fifth: 7  // Perfect 5th
    },
    swell: {
        duration: 4.0,
        velocity: 0.5
    },
    snowfall: {
        rangeMin: 84, // C6
        rangeMax: 108 // C8
    }
};

// --- UTILITIES ---

const fuzz = (value, variance, random) => {
    if (!random) return value;
    const range = variance * 2;
    return value + ((random.next() * range) - variance);
};

/**
 * Generates a driving String Ostinato (Spiccato).
 * @param {Object} chord - Current chord context.
 * @param {number} barStart - Start time of bar.
 * @param {string} patternType - 'driving', 'alberti', 'pulsing'
 * @param {Object} random - PRNG instance
 * @param {Object} context - Optional context including voiceRegistry, phaseOffset, and instrument
 */
export const generateStringOstinato = (chord, barStart, patternType = 'driving', random, context = {}) => {
    const events = [];
    const root = chord.rootPitchClass;
    const phaseOffset = context.phaseOffset || 0;
    const instrument = context.instrument;
    let third, fifth;
    if (chord.notes && chord.notes.length >= 3) {
        third = chord.notes[1];
        fifth = chord.notes[2];
    } else {
        // Fallback: Construct basic triad structure relative to root
        third = root + TEXTURE_CONFIG.fallback.third + 48;
        fifth = root + TEXTURE_CONFIG.fallback.fifth + 48;
    }

    // Normalize to mid-range (C3-C4)
    let baseRoot = root + 48; // C3 range

    // Registry Check: If Low End is muddy (e.g. Busy Bass), shift up
    if (context.voiceRegistry && isRangeBusy(context.voiceRegistry, barStart, 36, 55)) {
        // Bass active in C2-G3 range. Shift Ostinato to C4 (60) base.
        if (baseRoot < 60) baseRoot += 12;
    }

    // Determine loop limit and subdivision based on time signature
    // Default to 4/4 (16th notes = 0.25)
    let loopLimit = 16;
    let stepSize = 0.25; // 16th note default

    // Dynamic Density: Scale step size with Intensity
    const intensity = (context.intensity || 0.5);

    // Phase 4.3: Dynamic Vector Influence
    const rDensity = (context.vectors && context.vectors.rhythmicDensity !== undefined) ? context.vectors.rhythmicDensity : 0.5;
    // console.log(`DEBUG: TextureEngine rDensity=${rDensity}`);

    // Calculate dynamic thresholds
    const activeThreshold = Math.max(0.1, 0.4 - ((rDensity - 0.5) * 0.5));
    const climaxThreshold = Math.max(0.6, 0.9 - ((rDensity - 0.5) * 0.5));

    // VARIATION: Pattern Randomization (Seeded)
    if (patternType === 'driving') {
        const seed = (context.vectors && context.vectors.melodicDensity) ? context.vectors.melodicDensity * 100 : 0;
        const variants = ['straight', 'gallop', 'syncopated_16ths', 'interlocking_332'];
        const variantIndex = Math.floor(seed % variants.length); // Deterministic per generation
        context.ostinatoVariant = variants[variantIndex];
    }

    // New: Handle RhythmLibrary Array Patterns
    if (Array.isArray(patternType)) {
        stepSize = 0.25; // Force 16th grid for library patterns
    }

    if (patternType === 'driving' || patternType === 'pulsing' || Array.isArray(patternType)) {
        if (intensity < activeThreshold) stepSize = 0.5; // 8th
        else if (intensity > climaxThreshold) stepSize = 0.125; // 32nd
        else stepSize = 0.25; // 16th

        // Direct Vector Override (Rhythmic Density Slider)
        if (rDensity < 0.2) stepSize = 0.5; // Force 8th
        else if (rDensity > 0.8) stepSize = 0.125; // Force 32nd

        // Instrument Awareness: Breathing Room
        const family = instrument?.traits?.family || instrument?.family;
        if (family === 'woodwinds' || family === 'brass') {
            if (stepSize < 0.5) stepSize = 0.5;
        }
        if (instrument?.roles && Array.isArray(instrument.roles) && instrument.roles.includes('bass')) {
             if (stepSize < 0.5) stepSize = 0.5;
        }
    }

    if (chord.timeSig) {
         let num, den;
         if (Array.isArray(chord.timeSig)) {
             [num, den] = chord.timeSig;
         } else {
             num = chord.timeSig;
             den = 4;
         }

         if (den === 8 && num % 3 === 0) {
             stepSize = 0.5; // 8th note
             loopLimit = num;
         } else {
             const duration = (num * 4) / den;
             loopLimit = Math.floor(duration / stepSize);
         }
    } else {
        loopLimit = Math.floor(4.0 / stepSize);
    }

    // State Persistence for Note Coalescing
    let activeNote = null;

    for(let i=0; i<loopLimit; i++) {
        const stateIndex = (i + phaseOffset);
        const t = barStart + (i * stepSize);
        let note = baseRoot;
        let vel = 0.7;
        let isActiveStep = true; // Assume active unless filtered

        if (Array.isArray(patternType)) {
            const pIndex = i % patternType.length;
            const hit = patternType[pIndex];
            if (!hit) isActiveStep = false;

            if ((stateIndex % 16) >= 8) {
                const rootPC = root % 12;
                const fifthPC = fifth % 12;
                let interval = fifthPC - rootPC;
                if (interval < 0) interval += 12;
                note = baseRoot + interval;
            }

            const posInBeat = t % 1.0;
            if (posInBeat < 0.05) vel = fuzz(0.9, 0.05, random);
            else if (Math.abs(posInBeat - 0.5) < 0.05) vel = fuzz(0.8, 0.05, random);
            else vel = fuzz(0.6, 0.05, random);

            vel += (intensity - 0.5) * 0.3;
        }
        else if (patternType === 'driving') {
            const variant = context.ostinatoVariant || 'straight';
            if (variant === 'gallop') {
                const stepInBeat = i % 4; // 0,1,2,3
                if (stepInBeat === 1) isActiveStep = false;
            } else if (variant === 'interlocking_332') {
                const stepInCycle = i % 8; // 2 beat cycle
                if (stepInCycle === 0 || stepInCycle === 3 || stepInCycle === 6) vel += 0.2;
                else vel -= 0.2; // Ghost
            }

            const posInBeat = t % 1.0;
            if (posInBeat < 0.05) vel = fuzz(0.9, 0.05, random); // Beat
            else if (Math.abs(posInBeat - 0.5) < 0.05) vel = fuzz(0.8, 0.05, random); // &
            else vel = fuzz(0.6, 0.05, random); // e/a

            vel += (intensity - 0.5) * 0.3;
            if (chord.tension && chord.tension > 0.7) {
                vel = Math.min(1.0, vel + 0.15);
            }

            if ((stateIndex % 16) >= 8) {
                const rootPC = root % 12;
                const fifthPC = fifth % 12;
                let interval = fifthPC - rootPC;
                if (interval < 0) interval += 12;
                note = baseRoot + interval;
            }
        }
        else if (patternType === 'action') {
            const cyclePos = stateIndex % 16;
            const isHit = (cyclePos === 0 || cyclePos === 3 || cyclePos === 6 || cyclePos === 8 || cyclePos === 11 || cyclePos === 14);
            if (!isHit) isActiveStep = false;

            // Fix: Dynamic Scaling for Action Pattern (Prevent Flatline)
            let baseVel = 0.6 + (intensity * 0.3); // Range 0.6 - 0.9 depending on section intensity

            // Accents on 1 and 3 (Cycle 0 and 8 in 16th grid)
            if (cyclePos === 0 || cyclePos === 8) baseVel += 0.15;
            else baseVel -= 0.05;

            vel = fuzz(baseVel, 0.05, random);

            const actionBase = baseRoot - 12; // Drop octave
            note = actionBase;
            if (cyclePos >= 8) note = actionBase + 7; // 5th instead of Octave
        }
        else if (patternType === 'atmospheric') {
            if (i === 0) {
                events.push({
                    s: barStart,
                    d: baseRoot,
                    len: 4.0, // Full Bar
                    vel: fuzz(TEXTURE_CONFIG.swell.velocity, 0.1, random),
                    role: ABSTRACT_ROLES.HARMONY,
                    articulation: 'long',
                    swell: true
                });
            }
            continue;
        }
        else if (patternType === 'pulsing') {
            const isHit = (i % 2 === 0);
            if (!isHit) isActiveStep = false;
            note = baseRoot;
            vel = fuzz(0.7, 0.05, random);
            if (i % 4 === 0) vel += 0.1;
        }
        else if (patternType === 'hybrid_pulse') {
            const isHit = (i % 8 === 0 || i % 8 === 3 || i % 8 === 6);
            if (!isHit) isActiveStep = false;
            note = (i % 16 < 8) ? baseRoot : baseRoot + 12;
            vel = fuzz(0.85, 0.05, random);
        }
        else if (patternType === 'smoky_swells') {
            if (i === 0) {
                events.push({
                    s: barStart,
                    d: baseRoot,
                    len: fuzz(2.0, 0.1, random),
                    vel: fuzz(0.4, 0.05, random),
                    articulation: 'long',
                    role: ABSTRACT_ROLES.HARMONY
                });
            }
            if (i === 8) { // Beat 3
                 events.push({
                    s: barStart + 2.0,
                    d: third || baseRoot + 4,
                    len: fuzz(2.0, 0.1, random),
                    vel: fuzz(0.5, 0.05, random),
                    articulation: 'long',
                    role: ABSTRACT_ROLES.HARMONY
                });
            }
            continue;
        }
        else if (patternType === 'snowfall') {
            const offset = i % 4; // 0,1,2,3
            const tones = [baseRoot + 24, baseRoot + 19, baseRoot + 16, baseRoot + 12];
            note = tones[offset];
            if (!note) note = baseRoot + 12;
            vel = fuzz(0.6, 0.1, random);
        }
        else if (patternType === 'banjo_roll') {
            const tones = [baseRoot, baseRoot - 5, baseRoot + 4, baseRoot - 5];
            note = tones[i % 4];
            vel = (i % 4 === 0) ? fuzz(0.9, 0.05, random) : fuzz(0.7, 0.05, random);
        }

        if (instrument && instrument.range) {
            if (note < instrument.range.min) note += 12;
            if (note > instrument.range.max) note -= 12;
        }

        if (!isActiveStep) {
            // Flush active note if any
            if (activeNote) {
                events.push(activeNote);
                activeNote = null;
            }
            continue;
        }

        const articulation = (stepSize < 0.3) ? 'spiccato' : 'legato';

        // NOTE COALESCING LOGIC (The "Anti-Machine Gun" Fix)
        // If Legato intent, try to merge with previous note
        if (activeNote && articulation === 'legato' && activeNote.d === note) {
            // Check adjacency (tolerance 0.01)
            const expectedStart = activeNote.s + activeNote.len;
            if (Math.abs(t - expectedStart) < 0.01) {
                // Merge!
                activeNote.len += stepSize;
                // Keep original velocity (or average? Original is safer for sustain)
                continue;
            }
        }

        // Flush previous if not merged
        if (activeNote) {
            events.push(activeNote);
        }

        // Start new note
        activeNote = {
            s: t,
            d: note,
            len: stepSize,
            vel: Math.min(1.0, vel),
            role: ABSTRACT_ROLES.HARMONY,
            articulation: articulation
        };
    }

    // Flush remainder
    if (activeNote) events.push(activeNote);

    events.nextPhase = (context.phaseOffset || 0) + loopLimit;
    return events;
};

/**
 * STRATEGY: CLUSTER / SWARM (Horror)
 * Generates a massive sustained block chord (cluster).
 */
export const generateCluster = (chord, barStart, duration = 4.0) => {
    const events = [];
    if (!chord || !chord.notes) return events;

    chord.notes.forEach(note => {
        events.push({
            s: barStart,
            d: note,
            len: duration,
            vel: 0.8,
            articulation: 'tremolo',
            role: ABSTRACT_ROLES.HARMONY_PAD
        });
    });

    return events;
};

export const generateAtmosphericPad = (chord, barStart, random, context = {}) => {
    const events = [];
    const root = chord.rootPitchClass;

    const notes = [
        root + 48,
        root + 55,
        root + 60,
        root + 64
    ];

    if (chord.notes && chord.notes.length >= 3) {
        const sorted = [...chord.notes].sort((a,b) => a-b);
        const r = sorted[0];
        const third = sorted.find(n => (n-r)%12 === 3 || (n-r)%12 === 4) || (r+4);

        notes[0] = r + (Math.floor(48/12) - Math.floor(r/12))*12; // Transpose to C3 range
        notes[1] = notes[0] + 7;
        notes[2] = notes[0] + 12;
        notes[3] = notes[0] + 12 + (third - r); // 10th
    }

    events.push({
        s: barStart,
        d: notes[0],
        len: 4.0,
        vel: fuzz(0.5, 0.1, random),
        articulation: 'long',
        role: ABSTRACT_ROLES.HARMONY_PAD
    });

    events.push({
        s: barStart,
        d: notes[1],
        len: 1.9,
        vel: fuzz(0.4, 0.1, random),
        articulation: 'legato',
        role: ABSTRACT_ROLES.HARMONY_PAD
    });
    events.push({
        s: barStart + 2.0,
        d: notes[1],
        len: 1.9,
        vel: fuzz(0.45, 0.1, random),
        articulation: 'legato',
        role: ABSTRACT_ROLES.HARMONY_PAD
    });

    if (random && random.bool(0.7)) {
        events.push({
            s: barStart + 1.0,
            d: notes[3],
            len: 2.5,
            vel: fuzz(0.3, 0.1, random),
            articulation: 'long',
            swell: true,
            role: ABSTRACT_ROLES.HARMONY_COLOR
        });
    }

    return events;
};

export const generateBrokenChord = (chord, barStart, pattern = 'alberti', random, context = {}) => {
    const events = [];
    const root = chord.rootPitchClass;
    const instrument = context.instrument;

    let chordNotes = [];
    if (chord.notes && chord.notes.length >= 3) {
        chordNotes = [...chord.notes].sort((a,b) => a-b);
    } else {
        chordNotes = [root, root+4, root+7];
    }

    let baseOctave = 48;
    if (instrument) {
        if (instrument.id && (instrument.id.includes('harp') || instrument.id.includes('celesta'))) baseOctave = 60;
        if (instrument.roles && Array.isArray(instrument.roles) && instrument.roles.includes('bass')) {
            baseOctave = 36;
        }
    }

    chordNotes = chordNotes.map(n => (n % 12) + baseOctave);

    const isHarp = instrument && instrument.id.includes('harp');
    if (isHarp || pattern.includes('cascade')) {
        const l = chordNotes.length;
        for (let o = 1; o < 3; o++) {
            for(let j=0; j<l; j++) {
                chordNotes.push(chordNotes[j] + (12 * o));
            }
        }
        chordNotes = [...new Set(chordNotes)].sort((a,b) => a-b);
    } else {
        chordNotes.push(chordNotes[0] + 12);
    }

    const [low, mid, high, top] = chordNotes; // 4 notes approx

    let step = 0.5; // 8th notes
    let steps = 8;

    if (pattern.includes('cascade') || pattern === 'angels_harp') {
        step = 0.25; // 16ths
        steps = 16;
    }

    if (pattern === 'rolled_chord') {
        const chordSize = Math.min(4, chordNotes.length);
        for(let i=0; i<chordSize; i++) {
            events.push({
                s: barStart + (i * 0.05),
                d: chordNotes[i],
                len: 2.0,
                vel: fuzz(0.6, 0.1, random),
                articulation: 'long',
                role: ABSTRACT_ROLES.HARMONY_COLOR
            });
        }
        return events;
    }

    for(let i=0; i<steps; i++) {
        let note = low;

        if (pattern === 'alberti') {
            const phase = i % 4;
            const baseNotes = chordNotes.slice(0, 3);
            if (baseNotes.length < 3) continue;

            if (phase === 0) note = baseNotes[0];
            else if (phase === 1) note = baseNotes[2];
            else if (phase === 2) note = baseNotes[1];
            else note = baseNotes[2];
        }
        else if (pattern === 'harp_run') {
            note = chordNotes[i % chordNotes.length];
        }
        else if (pattern === 'cascade_up') {
            if (i >= chordNotes.length) break;
            note = chordNotes[i];
        }
        else if (pattern === 'cascade_down') {
            if (i >= chordNotes.length) break;
            note = chordNotes[chordNotes.length - 1 - i];
        }
        else if (pattern === 'angels_harp') {
            const n = chordNotes.length;
            const phase = i % (n * 2 - 2);
            const idx = (phase < n) ? phase : (n - 2) - (phase - n);
            note = chordNotes[idx];
        }
        else {
            const subset = chordNotes.slice(0, 4);
            const n = subset.length;
            const phase = i % (n * 2 - 2);
            const idx = (phase < n) ? phase : (n - 2) - (phase - n);
            note = subset[idx];
        }

        events.push({
            s: barStart + (i * step),
            d: note,
            len: step, // Full duration (was step * 0.9)
            vel: fuzz(0.6, 0.1, random),
            articulation: 'legato', // Explicit
            role: ABSTRACT_ROLES.HARMONY
        });
    }

    return events;
};

export const generateOrchestralSwell = (chord, barStart, random, length = 4.0) => {
    const events = [];
    const root = chord.rootPitchClass + 48; // C3

    const step = 0.125; // 32nd
    const count = Math.floor(length / step);

    for(let i=0; i<count; i++) {
        const progress = i / count;
        const vel = 0.2 + (Math.pow(progress, 2) * 0.8);

        events.push({
            s: barStart + (i * step),
            d: root,
            len: step,
            vel: vel,
            articulation: 'tremolo',
            role: 'swell'
        });

        if (progress > 0.5) {
             events.push({
                s: barStart + (i * step),
                d: root + 7,
                len: step,
                vel: vel * 0.9,
                articulation: 'tremolo',
                role: 'swell'
            });
        }
    }
    return events;
};

export const generateMotifComping = (chord, barStart, motifRhythm) => {
    const events = [];
    if (!motifRhythm || motifRhythm.length === 0) return events;
    const notes = chord.notes || [chord.rootPitchClass + 60];

    motifRhythm.forEach(hit => {
        notes.forEach(note => {
            events.push({
                s: barStart + hit.s,
                d: note,
                len: hit.l, // Full duration (was * 0.8)
                vel: 0.7 + (Math.random() * 0.1),
                articulation: 'marcato',
                role: ABSTRACT_ROLES.HARMONY
            });
        });
    });

    return events;
};

export const generateInterlockingRhythm = (chord, barStart, role) => {
    const events = [];
    const notes = chord.notes || [chord.rootPitchClass + 60];

    const callTimes = [0, 2];
    const responseTimes = [1, 3];
    const activeTimes = (role === 'call') ? callTimes : responseTimes;

    activeTimes.forEach(t => {
        notes.forEach(n => {
             events.push({
                s: barStart + t,
                d: n,
                len: 0.4, // Short burst
                vel: (role === 'call') ? 0.9 + (Math.random() * 0.05) : 0.75 + (Math.random() * 0.05),
                articulation: (role === 'call') ? 'marcato' : 'staccato',
                role: 'hocket'
            });
        });
    });
    return events;
};

export const generateWoodwindRun = (chord, barStart, scale, random) => {
    if (!chord) return [];
    const events = [];
    if (!scale || scale.length === 0) return [];

    let beatsPerBar = 4;
    if (chord.timeSig) {
        let num, den;
        if (Array.isArray(chord.timeSig)) {
            [num, den] = chord.timeSig;
        } else {
            num = chord.timeSig;
            den = 4;
        }
        beatsPerBar = num;
        if (den === 8) {
            const totalDuration = (num * 4) / den;
            beatsPerBar = totalDuration;
        }
    }

    const runStart = barStart + Math.max(0, beatsPerBar - 1.0);
    const targetStart = (chord.rootPitchClass % 12) + 72;
    const scaleNotes = scale.map(pc => pc % 12).sort((a,b) => a-b);

    let currentOctave = Math.floor(targetStart / 12);
    let startIndex = 0;

    const steps = 8;
    const stepSize = 0.125;

    for(let i=0; i<steps; i++) {
        const t = runStart + (i * stepSize);

        const degreeIndex = startIndex + i;
        const degreeInScale = degreeIndex % scaleNotes.length;
        const octaveOffset = Math.floor(degreeIndex / scaleNotes.length);

        const notePC = scaleNotes[degreeInScale];
        const note = notePC + ((currentOctave + octaveOffset) * 12);

        events.push({
            s: t,
            d: note,
            len: stepSize, // Full duration
            vel: Math.min(1.0, (0.6 + (i * 0.05)) + fuzz(0, 0.05, random)),
            role: ABSTRACT_ROLES.COUNTER_MELODY,
            articulation: 'legato'
        });
    }
    return events;
};

export const generateBrassStabs = (chord, barStart, random) => {
    const events = [];
    const hits = [0, 1.5, 3];

    hits.forEach(h => {
        events.push({
            s: barStart + h,
            d: chord.rootPitchClass + 48,
            notes: chord.notes,
            len: 0.25, // Full duration
            vel: fuzz(0.95, 0.03, random),
            role: ABSTRACT_ROLES.HARMONY,
            articulation: 'marcato'
        });
    });
    return events;
};

export const generatePolyrhythm = (chord, barStart, ratio = '3:2') => {
    if (!chord) return [];
    const events = [];

    const notes = (chord.notes && chord.notes.length > 0)
        ? chord.notes.map(n => n % 12 + 60)
        : [chord.rootPitchClass + 60];

    if (notes.length === 1) notes.push(notes[0] + 7);
    if (notes.length === 2) notes.push(notes[0] + 12);
    if (notes.length === 3) notes.push(notes[1] + 12);

    let noteCount = 0;
    let duration = 0;

    if (ratio === '5:4') {
        noteCount = 5;
        if (chord.timeSig) {
            let num, den;
            if (Array.isArray(chord.timeSig)) {
                [num, den] = chord.timeSig;
            } else {
                num = chord.timeSig;
                den = 4;
            }
            duration = (num * 4) / den;
        } else {
            duration = 4.0;
        }
    } else {
        noteCount = 3;
        duration = 2.0;
    }

    const stepSize = duration / noteCount;
    const iterations = (ratio === '3:2') ? 2 : 1;

    for (let iter = 0; iter < iterations; iter++) {
        const iterStart = barStart + (iter * duration);
        for (let i = 0; i < noteCount; i++) {
            const time = iterStart + (i * stepSize);
            const note = notes[i % notes.length];

            events.push({
                s: time,
                d: note,
                len: stepSize, // Full duration
                vel: Math.min(1.0, 0.6 + (i === 0 ? 0.2 : 0) + fuzz(0, 0.05, null)),
                articulation: 'legato',
                role: 'polyrhythm_layer'
            });
        }
    }

    return events;
};
